/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useRef } from "react";
import { useCrmStore } from "../../../stores/crm";
import useModals from "../../../hooks/useModals";
import DeleteModal from "../modals/DeleteModal";
import { TableProps } from "../../../interfaces/table";
import { getGeneralLedger } from "../../../api/customerFile";
import {
  createPdfBlob,
  downloadPdf,
  openPdfInNewWindow,
} from "../../../services/function";
import { downloadCreditNote } from "../../../api/creditNotes";
import { showToastError } from "../../../services/toast";
import { getInvoicePdf, getProformaPdf } from "../../../api/invoices";
import DocumentsDeposit from "../../crm/DocumentsDeposit";
import { useUtilsStore } from "../../../stores/components/utils";
import DisplayDetail from "../buttons/DisplayDetail";
import {
  Badge,
  DesktopTable,
  DocumentIcon,
  DownloadIcon,
  EyeIcon,
  IconButton,
  LedgerIcon,
  LinkedRowSummary,
  MicroLabel,
  MobileCardFooter,
  MobileCardList,
  MobileField,
  MobileFieldGrid,
  TableEmptyState,
  TableFrame,
  Td,
  Th,
  TrashIcon,
  cardClasses,
  getNumericAccessors,
  renderCellValue,
  rowClasses,
} from "./tableUi";

const TableCRM = ({ columns, data }: TableProps) => {
  // Store
  const {
    tabCRM,
    setTabCRM,
    setViewOrUpdate,
    setIdCustomer,
    setClientName,
    setIdMission,
  } = useCrmStore();
  // Modals
  const { modals, openModals, openModal, closeModal } = useModals();
  const { searchTerm } = useUtilsStore();

  // Ajout de la ref pour le conteneur du tableau
  const tableContainerRef = useRef<HTMLDivElement>(null);

  // Fonction pour gérer le clic sur une ligne
  const handleRowClick = (id: number) => {
    // Permet de gérer l'affichage de la mission ou des documents
    if (tabCRM === "Dossier clients") {
      const client = data.find((client) => client.id === id);
      setClientName(
        client?.name ||
          `${client?.firstName || ""} ${client?.lastName || ""}`.trim()
      );
      setIdCustomer(id);
      setTabCRM("Missions");
    } else if (tabCRM === "Missions") {
      setIdMission(id);
      setTabCRM("Documents");
    }
  };

  const handlePdfOperation = async (
    pdfFunction: () => Promise<any>,
    operation: (blob: Blob, filename?: string) => void,
    filename?: string,
    errorMessage: string = "Erreur lors du téléchargement du fichier"
  ) => {
    try {
      const response = await pdfFunction();
      const blob = createPdfBlob(response.data);
      operation(blob, filename);
    } catch {
      showToastError(errorMessage);
    }
  };

  const handleDownloadGrandLivre = (id: number) => {
    handlePdfOperation(
      () => getGeneralLedger(id),
      openPdfInNewWindow,
      undefined,
      "Erreur lors du téléchargement du grand livre"
    );
  };

  const handleViewCreditNotes = (id: number) => {
    handlePdfOperation(
      () => downloadCreditNote(id),
      openPdfInNewWindow,
      undefined,
      "Erreur lors de la visualisation de l'avoir"
    );
  };

  const handleDownloadCreditNote = (id: number, name: string) => {
    handlePdfOperation(
      () => downloadCreditNote(id),
      (blob) => downloadPdf(blob, `avoir_${name}`),
      undefined,
      "Erreur lors du téléchargement de l'avoir"
    );
  };

  const handleViewInvoice = (id: number) => {
    handlePdfOperation(
      () => getProformaPdf(id),
      openPdfInNewWindow,
      undefined,
      "Erreur lors de la visualisation de la facture"
    );
  };

  const handleDownloadInvoice = (id: number, name: string) => {
    handlePdfOperation(
      () => getInvoicePdf(id),
      (blob) => downloadPdf(blob, name),
      undefined,
      "Erreur lors du téléchargement de la facture"
    );
  };

  useEffect(() => {
    if (searchTerm !== "" && tableContainerRef.current) {
      tableContainerRef.current.scrollTop = 0;
    }
  }, [searchTerm]);

  const numericAccessors = getNumericAccessors(columns, data);
  const dataColumns = columns.filter((col) => col.accessor !== "actions");
  const hasDetailColumn = columns.some((col) => col.accessor === "actions");
  const [primaryColumn, ...secondaryColumns] = dataColumns;

  const renderDetailButton = (row: Record<string, any>) => (
    <DisplayDetail
      id={row.id}
      idSetter={
        tabCRM === "Dossier clients"
          ? setIdCustomer
          : tabCRM === "Missions"
            ? setIdMission
            : () => {}
      }
      setter={setViewOrUpdate}
      clientName={
        row?.name || `${row?.firstName || ""} ${row?.lastName || ""}`.trim()
      }
      clientNameSetter={setClientName}
    />
  );

  // Actions d'une ligne, partagées entre la table et les cartes
  const renderRowActions = (row: Record<string, any>) => (
    <div className="flex items-center gap-1.5">
      {/* Affiche les boutons visualisation et téléchargement des documents */}
      {tabCRM === "Documents" ? (
        <>
          <IconButton
            label="Visualiser"
            tone="cyan"
            onClick={(e) => {
              e.stopPropagation();
              handleViewInvoice(row.id);
            }}
          >
            <EyeIcon />
          </IconButton>
          <IconButton
            label="Télécharger"
            tone="blue"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadInvoice(row.id, row.documentFileName);
            }}
          >
            <DownloadIcon />
          </IconButton>
        </>
      ) : null}

      {/* Affiche les boutons du grand livre et documents dans les dossiers clients */}
      {tabCRM === "Dossier clients" && (
        <>
          <IconButton
            label="Documents"
            tone="purple"
            onClick={(e) => {
              e.stopPropagation();
              openModal(
                "depotDocuments",
                <DocumentsDeposit
                  closeModal={() => closeModal("depotDocuments")}
                  idCustomer={row.id}
                />
              );
            }}
          >
            <DocumentIcon />
          </IconButton>
          <IconButton
            label="Grand livre"
            tone="indigo"
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadGrandLivre(row.id);
            }}
          >
            <LedgerIcon />
          </IconButton>
        </>
      )}

      {/* Affiche le bouton de suppression sauf pour les documents */}
      {tabCRM !== "Documents" && (
        <IconButton
          label="Supprimer"
          tone="danger"
          onClick={(e) => {
            e.stopPropagation();
            openModal(
              "deleteModal",
              <DeleteModal
                closeModal={() => closeModal("deleteModal")}
                id={row.id}
              />
            );
          }}
        >
          <TrashIcon />
        </IconButton>
      )}
    </div>
  );

  const renderCreditNoteActions = (creditNote: any) => (
    <div className="flex items-center gap-1.5">
      <IconButton
        label="Visualiser l'avoir"
        tone="cyan"
        onClick={(e) => {
          e.stopPropagation();
          handleViewCreditNotes(creditNote.id);
        }}
      >
        <EyeIcon />
      </IconButton>
      <IconButton
        label="Télécharger l'avoir"
        tone="blue"
        onClick={(e) => {
          e.stopPropagation();
          handleDownloadCreditNote(creditNote.id, creditNote.creditNoteNumber);
        }}
      >
        <DownloadIcon />
      </IconButton>
    </div>
  );

  const renderCreditNoteSummary = (creditNote: any) => (
    <LinkedRowSummary
      label="Avoir"
      reference={`#${creditNote.creditNoteNumber}`}
      amountLabel="Montant TTC"
      amount={`${creditNote.amount}€`}
    />
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Affichage des modales en fonction de openModals */}
      {Object.keys(modals).map((modalId) =>
        openModals[modalId] ? <div key={modalId}>{modals[modalId]}</div> : null
      )}

      <TableFrame scrollRef={tableContainerRef}>
        {data.length === 0 ? (
          <TableEmptyState
            title="Aucune donnée disponible"
            description={
              searchTerm !== ""
                ? "Aucun résultat ne correspond à votre recherche."
                : "Les données apparaîtront ici une fois ajoutées."
            }
          />
        ) : (
          <>
            {/* Vue tableau — écrans larges */}
            <DesktopTable>
              <thead className="sticky top-0 z-10">
                <tr>
                  {columns.map((col, index) => (
                    <Th
                      key={index}
                      align={numericAccessors.has(col.accessor) ? "right" : "left"}
                      width={index === 0 ? "26%" : undefined}
                    >
                      {col.header}
                    </Th>
                  ))}
                  <Th align="right" className="w-px">
                    Actions
                  </Th>
                </tr>
              </thead>
              <tbody className="font-Nunito">
                {data.map((row, rowIndex) => {
                  const hasCreditNotes = row?.creditNotes?.length > 0;
                  const isClosed =
                    tabCRM === "Missions" && row?.closed === true;

                  return (
                    <React.Fragment key={rowIndex}>
                      {/* Ligne principale */}
                      <tr
                        onClick={() => handleRowClick(row.id)}
                        className={rowClasses({
                          highlighted: hasCreditNotes,
                          muted: isClosed,
                        })}
                      >
                        {/* Affiche les données de chaque colonne en fonction de l'accessor défini dans columns */}
                        {columns.map((col, colIndex) => (
                          <Td
                            key={colIndex}
                            align={
                              numericAccessors.has(col.accessor)
                                ? "right"
                                : "left"
                            }
                            className={
                              colIndex === 0
                                ? "font-semibold text-slate-900"
                                : isClosed
                                  ? "text-slate-400"
                                  : "text-slate-600"
                            }
                          >
                            {col.accessor === "actions" ? (
                              renderDetailButton(row)
                            ) : colIndex === 0 ? (
                              <div className="flex items-center gap-2">
                                <span className="truncate">
                                  {renderCellValue(row[col.accessor])}
                                </span>
                                {isClosed && <Badge>Clôturée</Badge>}
                              </div>
                            ) : (
                              renderCellValue(row[col.accessor])
                            )}
                          </Td>
                        ))}

                        <Td>
                          <div className="flex justify-end">
                            {renderRowActions(row)}
                          </div>
                        </Td>
                      </tr>

                      {/* Lignes des avoirs liés */}
                      {tabCRM === "Documents" &&
                        row?.creditNotes?.map(
                          (creditNote: any, creditIndex: any) => (
                            <tr
                              key={`${rowIndex}-credit-${creditIndex}`}
                              className="bg-amber-50/40"
                            >
                              <Td colSpan={columns.length} dense indent>
                                {renderCreditNoteSummary(creditNote)}
                              </Td>
                              <Td dense>
                                <div className="flex justify-end">
                                  {renderCreditNoteActions(creditNote)}
                                </div>
                              </Td>
                            </tr>
                          )
                        )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </DesktopTable>

            {/* Vue cartes — mobile et tablette */}
            <MobileCardList>
              {data.map((row, rowIndex) => {
                const hasCreditNotes = row?.creditNotes?.length > 0;
                const isClosed = tabCRM === "Missions" && row?.closed === true;

                return (
                  <li key={rowIndex}>
                    <div
                      onClick={() => handleRowClick(row.id)}
                      className={cardClasses({
                        highlighted: hasCreditNotes,
                        muted: isClosed,
                      })}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <MicroLabel>{primaryColumn?.header}</MicroLabel>
                          <p
                            className={`mt-0.5 text-[15px] leading-snug font-bold wrap-break-word ${
                              isClosed ? "text-slate-500" : "text-slate-900"
                            }`}
                          >
                            {primaryColumn
                              ? renderCellValue(row[primaryColumn.accessor])
                              : null}
                          </p>
                          {isClosed && (
                            <div className="mt-1.5">
                              <Badge>Clôturée</Badge>
                            </div>
                          )}
                        </div>
                        {hasDetailColumn ? renderDetailButton(row) : null}
                      </div>

                      {secondaryColumns.length > 0 && (
                        <MobileFieldGrid>
                          {secondaryColumns.map((col) => (
                            <MobileField
                              key={col.accessor}
                              label={col.header}
                              value={renderCellValue(row[col.accessor])}
                              numeric={numericAccessors.has(col.accessor)}
                              muted={isClosed}
                            />
                          ))}
                        </MobileFieldGrid>
                      )}

                      <MobileCardFooter>
                        {renderRowActions(row)}
                      </MobileCardFooter>
                    </div>

                    {/* Avoirs liés */}
                    {tabCRM === "Documents" &&
                      row?.creditNotes?.map(
                        (creditNote: any, creditIndex: any) => (
                          <div
                            key={`${rowIndex}-credit-${creditIndex}`}
                            className="mt-1.5 ml-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-amber-200 bg-amber-50/60 p-3"
                          >
                            {renderCreditNoteSummary(creditNote)}
                            {renderCreditNoteActions(creditNote)}
                          </div>
                        )
                      )}
                  </li>
                );
              })}
            </MobileCardList>
          </>
        )}
      </TableFrame>
    </div>
  );
};

export default TableCRM;
